/**
 * Where to land after switching company.
 *
 * Every id in this product is company-scoped: a candidate, a document, a
 * vehicle, a workflow instance. So a URL carrying one CANNOT resolve in
 * another company — it is not a maybe, and there is nothing to try. The
 * switcher used to reload the page it was on, which meant switching from
 * a candidate landed on that candidate's URL under the new company and
 * said "Could not open that candidate".
 *
 * Deliberately no probe request first. A round trip that can only come
 * back 404 buys nothing but a slower switch and a flash of an error, and
 * a "maybe it exists" fallback would be worse: the same id resolving in
 * two companies is the one outcome that must never happen.
 *
 * The rule is therefore structural: a path made only of STATIC segments
 * survives, and the first dynamic segment sends you to that section's
 * root. Written as a whitelist of known static sub-paths rather than as
 * "does this look like a Mongo id", because ids are not always 24 hex
 * characters — a service area is a number, and a roster's owner type is a
 * word.
 */

/** Section root → where that section is worth landing. */
const SECTION_HOME: Record<string, string> = {
  dashboard: '/dashboard',
  hiring: '/hiring',
  files: '/files',
  fleet: '/fleet',
  operations: '/operations',
  tasks: '/tasks',
  users: '/users',
  coverage: '/coverage',
  settings: '/settings',
  companies: '/companies',
  schedule: '/schedule',
};

/**
 * Sub-paths that name a VIEW rather than a record. These carry no id, so
 * they are as true in the new company as they were in the old one.
 */
const STATIC_SUBPATHS: Record<string, string[]> = {
  hiring: ['list', 'follow-up', 'closed', 'insights'],
  files: ['compliance', 'search', 'recently-deleted'],
  settings: ['bug-reports'],
  schedule: ['timeline', 'usual'],
};

const DEFAULT_HOME = '/dashboard';

export function landingAfterSwitch(pathname: string): string {
  const parts = pathname.split('/').filter(Boolean);
  if (parts.length === 0) return DEFAULT_HOME;

  const [section, ...rest] = parts;
  const home = SECTION_HOME[section];

  // A section this rule has never heard of is not a section to gamble on.
  if (!home) return DEFAULT_HOME;

  // The section root itself: it lists whatever the new company has.
  if (rest.length === 0) return home;

  // One static segment deeper — a view, not a record.
  if (rest.length === 1 && (STATIC_SUBPATHS[section] || []).includes(rest[0])) {
    return `${home}/${rest[0]}`;
  }

  // Anything else names something that belongs to the company being left.
  return home;
}

/** Whether switching company will move you — for wording the confirmation. */
export const switchWillMove = (pathname: string) =>
  landingAfterSwitch(pathname) !== pathname;
